import type { SearchTileDensity } from 'components/SearchKit/SearchTiles/searchTileDensity'
import { getSearchTileDensityClassName } from 'components/SearchKit/SearchTiles/searchTileDensity'
import { useMetaSearchMobileV2Enabled } from 'components/Pages/HotelSearchPage/useMetaSearchMobileV2'
import clsx from 'clsx'
import { rem } from 'polished'
import type { PropsWithChildren, ReactNode } from 'react'
import styled, { css } from 'styled-components'
import { _searchTileContainerQueryUp } from './_MetaSearchTileBase'
import { searchTileContentSpacingStyles } from './searchTileLayoutStyles'

const Container = styled.div<{ $anchorPricingToBottom: boolean }>`
  grid-area: content;
  display: flex;
  flex-direction: column;
  min-height: auto;
  ${searchTileContentSpacingStyles}

  &.density-compact {
    min-height: ${rem(208)};
  }

  &.hugContent {
    min-height: unset;
    height: max-content;
    align-self: start;

    ${_searchTileContainerQueryUp.tablet} {
      ${props => props.$anchorPricingToBottom && css`
        height: auto;
        align-self: stretch;
      `}
    }
  }
`

const ContentContainer = styled.div<{ $anchorPricingToBottom: boolean }>`
  flex-grow: 1;
  grid-area: content;
  display: grid;
  min-width: 0;
  gap: ${rem(16)};
  grid-template:
    "content-primary" auto
    "content-secondary" min-content / 1fr;

  &.density-compact {
    gap: ${rem(12)};
    grid-template:
      "content-primary" auto
      "content-secondary" auto / 1fr;
    align-items: start;
  }

  & > * {
    min-width: 0;
  }

  &.hugContent {
    flex-grow: 0;
    align-content: start;

    ${_searchTileContainerQueryUp.tablet} {
      ${props => props.$anchorPricingToBottom && css`
        flex-grow: 1;
        align-content: stretch;
      `}
    }
  }

  ${_searchTileContainerQueryUp.tablet} {
    grid-template: "content-primary content-secondary" 1fr / minmax(0, 1fr) ${rem(432)};

    &.density-compact {
      grid-template:
        "content-primary" 1fr
        "content-secondary" auto / 1fr;
      align-items: start;
    }
  }

  ${_searchTileContainerQueryUp.desktopSmall} {
    grid-template:
      "content-primary" auto
      "content-secondary" min-content / 1fr;
  }

  ${_searchTileContainerQueryUp.desktopLarge} {
    grid-template: "content-primary content-secondary" 1fr / minmax(0, 1fr) ${rem(432)};

    &.density-compact {
      grid-template:
        "content-primary" 1fr
        "content-secondary" auto / 1fr;
    }
  }
`

const Extension = styled.div`
  &:empty {
    display: none;
  }
`

interface Props extends PropsWithChildren {
  /** Forces pricing to the bottom of the available tablet/desktop tile height. */
  anchorPricingToBottom?: boolean
  /**
   * Supporting content that always sits at the bottom of the content
   */
  bottomExtension?: ReactNode
  /**
   * Supporting content that always sits at the top of the content
   */
  topExtension?: ReactNode
  /**
   * @default default
   */
  density?: SearchTileDensity
}

function MetaSearchTileContent({
  children,
  anchorPricingToBottom = false,
  bottomExtension,
  topExtension,
  density = 'default',
}: Props) {
  const hugContent = useMetaSearchMobileV2Enabled()
  const densityClassName = getSearchTileDensityClassName(density)

  return <Container $anchorPricingToBottom={anchorPricingToBottom} className={clsx(densityClassName, { hugContent })}>
    {!!topExtension && <Extension>{topExtension}</Extension>}
    <ContentContainer
      $anchorPricingToBottom={anchorPricingToBottom}
      className={clsx('_MetaSearchTileContent', densityClassName, { hugContent })}
    >
      {children}
    </ContentContainer>
    {!!bottomExtension && <Extension>{bottomExtension}</Extension>}
  </Container>
}

export default MetaSearchTileContent
